/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/systemMessages.scss';
import {AwesomeIcon, Link} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import React, {MouseEventHandler} from 'react';
import {useTranslation} from 'react-i18next';
import SystemMessage from '../../models/systemMessageModel';
import {SystemMessageType} from '../../util/interfaces';
import {IconName} from '@fortawesome/fontawesome-svg-core';
import IDate from '@dvrd/idate';
import ApiKey from '../../models/apiKeyModel';

interface Props {
    onDismissMessage: (messageId: string) => MouseEventHandler;
    onRenewApi: (accountID: string) => MouseEventHandler;
    onDismissAPIExpiry: (apiKey: ApiKey) => MouseEventHandler;
    messages: Array<SystemMessage>;
    expiringAPIKeys: Array<ApiKey>;
}

export default function SystemMessages(props: Props) {
    const {t} = useTranslation();
    const {messages, onDismissMessage, expiringAPIKeys, onRenewApi, onDismissAPIExpiry} = props;
    if (!messages.length && !expiringAPIKeys.length) return null;

    function getRenderMessages(): SystemMessage[] {
        const errorMessage = getFirstMessage(SystemMessageType.ERROR);
        const warningMessage = getFirstMessage(SystemMessageType.WARNING);
        const infoMessage = getFirstMessage(SystemMessageType.INFO);
        const confirmMessage = getFirstMessage(SystemMessageType.CONFIRMATION);
        const _messages: SystemMessage[] = [];
        if (errorMessage) _messages.push(errorMessage);
        if (warningMessage) _messages.push(warningMessage);
        if (infoMessage) _messages.push(infoMessage);
        if (confirmMessage) _messages.push(confirmMessage);
        return _messages;
    }

    function getFirstMessage(messageType: SystemMessageType): SystemMessage | null {
        const remaining = messages.filter((message: SystemMessage) => message.messageType === messageType);
        if (!remaining.length) return null;
        return remaining.sort(
            (messageA: SystemMessage, messageB: SystemMessage) => messageB.priority - messageA.priority
        )[0];
    }

    function renderMessages() {
        const _messages = getRenderMessages();
        if (!_messages.length) return null;
        return _messages.map((message: SystemMessage) => (
            <div key={message.id} className={classNames('system-message', message.messageType.toLowerCase())}>
                <p className='message'>
                    {renderIcon(message.messageType)}
                    {message.message}
                </p>
                <AwesomeIcon
                    name='times'
                    className='dismiss'
                    onClick={onDismissMessage(message.id)}
                    title={t('systemMessage.close', 'Sluiten')}
                />
            </div>
        ));
    }

    function renderIcon(messageType: SystemMessageType) {
        let iconName: IconName | null = null;
        if (messageType === SystemMessageType.INFO) iconName = 'info-circle';
        else if (messageType === SystemMessageType.ERROR) iconName = 'exclamation-circle';
        else if (messageType === SystemMessageType.CONFIRMATION) iconName = 'check-circle';
        else if (messageType === SystemMessageType.WARNING) iconName = 'exclamation-triangle';
        if (!iconName) return null;
        return <AwesomeIcon name={iconName} className='message-icon' />;
    }

    function renderApiExpiry(apiKey: ApiKey) {
        const expiresInDays = apiKey.refreshExpiresAt!.diff(IDate.now(), 'days');
        const messageType = expiresInDays < 7 ? SystemMessageType.ERROR : SystemMessageType.WARNING;
        const expiryText =
            expiresInDays < 0
                ? t('systemMessage.expired', 'is verlopen')
                : t('systemMessage.expiresInDays', 'verloopt over {{days}} dagen', {days: expiresInDays});
        const accountLabel = apiKey.account ? ` ${apiKey.account.labelWithSellerID} ` : ' ';
        return (
            <div className={`system-message api-expiry ${messageType.toLowerCase()}`}>
                <p className='message'>
                    {renderIcon(messageType)}
                    {t('systemMessage.apiExpiry.prefix', 'De koppeling met jouw Bol account')}
                    {accountLabel}
                    {expiryText}.{' '}
                    {t(
                        'systemMessage.apiExpiry.suffix',
                        'Om te voorkomen dat bestellingen niet meer verwerkt worden, verzoeken we je om de koppeling te vernieuwen.'
                    )}{' '}
                    <Link onClick={onRenewApi(apiKey.accountId)}>
                        {t('systemMessage.apiExpiry.renew', 'Nu vernieuwen')}
                    </Link>
                </p>
                <AwesomeIcon
                    name='times'
                    className='dismiss'
                    onClick={onDismissAPIExpiry(apiKey)}
                    title={t('systemMessage.hideNotification', 'Melding verbergen')}
                />
            </div>
        );
    }

    return (
        <div className='system-messages'>
            {renderMessages()}
            {expiringAPIKeys.map(renderApiExpiry)}
        </div>
    );
}
